import { Button, Space, Table, Tag, Modal } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { Bounce, toast } from 'react-toastify';
import moment from 'moment';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthRequest from '../../../../services/requests.service';
import {EditOutlined, DeleteOutlined, PauseCircleOutlined, PlayCircleOutlined} from '@ant-design/icons';

const { confirm } = Modal;

const OffersTable = React.memo(({ datas, setIsModalOpen, setCurrentDeal, currentPage, pageSize, total, onPageChange }) => {
  const request = useAuthRequest();
  const queryClient = useQueryClient();

  // Mutation pour supprimer une offre
  const deleteOfferMutation = useMutation({
    mutationFn: async (offerId) => {
      const res = await request.deleteDataAPI(`/deal/${offerId}`);
      return res;
    },
    onSuccess: (_, offerId) => {
      // Invalider le cache des deals pour forcer un refetch
      queryClient.invalidateQueries(['dealsAll']);
      
      toast.success("Offre supprimée!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    },
    onError: (error) => {
      toast.error(error.msg || "Erreur lors de la suppression de l'offre", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  });

  // Mutation pour activer/désactiver une offre
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ offerId, active }) => {
      const res = await request.patchDataAPI(`/deal/${offerId}`, { active });
      return res;
    },
    onSuccess: (_, { active }) => {
      queryClient.invalidateQueries(['dealsAll']);

      toast.success(active ? "Offre activée!" : "Offre désactivée!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    },
    onError: (error) => {
      toast.error(error.msg || "Erreur lors de la mise à jour de l'offre", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  });

  const CustomHeader = useCallback((props) => {
    return (
      <th {...props} className="bg-blue-500 text-white p-2 text-left">
        {props.children}
      </th>
    );
  }, []);

  const handleUpdate = useCallback((record) => {
    setIsModalOpen(true);
    setCurrentDeal(record);
  }, [setIsModalOpen, setCurrentDeal]);

  const handleToggleActive = useCallback((record) => {
    const isActive = record.active !== false;
    toggleActiveMutation.mutate({ offerId: record._id, active: !isActive });
  }, [toggleActiveMutation]);

  const handleDelete = useCallback((record) => {
    confirm({
      title: 'Êtes-vous sûr de vouloir supprimer cette offre?',
      content: 'Cette action est irréversible',
      okText: 'Oui',
      okType: 'danger',
      cancelText: 'Non',
      centered: true,
      onOk: async () => {
        // Utiliser la mutation pour supprimer l'offre
        deleteOfferMutation.mutate(record._id);
      },
    });
  }, [deleteOfferMutation]);

  const renderCompany = useCallback((text) => <span>{text.name}</span>, []);

  const renderTags = useCallback((_, { tagsDescription }) => (
    <>
      {tagsDescription &&
        tagsDescription.length > 0 &&
        tagsDescription.map((tag) => {
          let color = tag.length > 5 ? 'geekblue' : 'green';
          if (tag === 'loser') {
            color = 'volcano';
          }
          return (
            <Tag color={color} key={tag}>
              {tag.toUpperCase()}
            </Tag>
          );
        })}
    </>
  ), []);

  const renderCreatedAt = useCallback((createdAt) => (
    <span>{moment(createdAt).format('DD/MM/YYYY')}</span>
  ), []);

  const renderActions = useCallback((_, record) => {
    const isActive = record.active !== false;
    return (
      <Space size="middle">
        <Button
          className='w-10 h-10'
          onClick={() => handleUpdate(record)}
          disabled={deleteOfferMutation.isPending}
        >
          <EditOutlined/>
        </Button>
        <Button
          className='w-10 h-10'
          onClick={() => handleToggleActive(record)}
          loading={toggleActiveMutation.isPending && toggleActiveMutation.variables?.offerId === record._id}
          disabled={toggleActiveMutation.isPending}
          title={isActive ? 'Désactiver' : 'Activer'}
        >
          {isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined style={{ color: '#52c41a' }} />}
        </Button>
        <Button
          className='w-10 h-10'
          danger
          onClick={() => handleDelete(record)}
          loading={deleteOfferMutation.isPending}
          disabled={deleteOfferMutation.isPending}
        >
          <DeleteOutlined/>
        </Button>
      </Space>
    );
  }, [handleUpdate, handleDelete, handleToggleActive, deleteOfferMutation.isPending, toggleActiveMutation.isPending, toggleActiveMutation.variables]);

  const columns = useMemo(() => [
    {
      title: 'Entreprise',
      dataIndex: 'company',
      key: 'company',
      render: renderCompany,
    },
    {
      title: 'Offres',
      dataIndex: 'offer',
      key: 'offer',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Tags',
      key: 'tagsDescription',
      dataIndex: 'tagsDescription',
      render: renderTags,
    },
    {
      title: 'Date Publication',
      dataIndex: 'createdAt',
      key: 'createdAt',
      className: 'hide-on-mobile',
      render: renderCreatedAt,
    },
    {
      title: 'Actions',
      key: 'action',
      render: renderActions,
    },
  ], [renderCompany, renderTags, renderCreatedAt, renderActions]);

  const tableComponents = useMemo(() => ({
    header: {
      cell: CustomHeader,
    },
  }), [CustomHeader]);

  const pagination = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: total,
    onChange: onPageChange,
  }), [currentPage, pageSize, total, onPageChange]);

  return (
    <Table
      columns={columns}
      dataSource={datas.deals}
      className="bg-white dark:bg-slate-800 shadow rounded-lg"
      components={tableComponents}
      pagination={pagination}
      loading={deleteOfferMutation.isPending}
    />
  );
});

OffersTable.displayName = 'OffersTable';

export default OffersTable;
